import { useQueries, useQuery } from '@tanstack/react-query';

import {
  getDashboardSocieteSettings,
  getEmployeeQualifyingOrderCount,
} from '~/services/BusinessTraveller/BusinessDashboardService';
import { getLuxLoyaltyAccount } from '~/services/LuxLoyalty';

import { createConcurrencyLimiter } from '~/utils/createConcurrencyLimiter';
import { isLuxLoyaltyEnabled } from '~/utils/luxLoyalty';

// Shared across expanded businesses and pages; cached employee queries keep their existing identity.
const runLimited = createConcurrencyLimiter(4);

function employeeSocieteAccountQuery(customerId: string) {
  return {
    queryKey: ['business-traveller', 'employee-societe-account', customerId],
    queryFn: ({ signal }: { signal: AbortSignal }) => {
      if (!isLuxLoyaltyEnabled()) throw new Error('Société status is unavailable.');
      return runLimited(() => getLuxLoyaltyAccount(customerId), signal);
    },
    staleTime: 60_000,
    retry: false,
  };
}

// Check all employees before filtering/pagination, without requesting qualification counts for hidden rows.
export function useEmployeeSocieteStatuses(customerIds: Array<string>, enabled: boolean) {
  const queries = useQueries({
    queries: customerIds.map((customerId) => ({ ...employeeSocieteAccountQuery(customerId), enabled })),
  });
  return queries.map((query, index) => ({ customerId: customerIds[index], query }));
}

export function useDashboardSocieteSettings(businessId: string) {
  return useQuery({
    queryKey: ['business-traveller', 'societe-settings', businessId],
    queryFn: () => getDashboardSocieteSettings(businessId),
    staleTime: 60_000,
    retry: false,
  });
}

export function useEmployeeSocieteProgress(businessId: string, employee?: App.BusinessEmployee) {
  return useQuery({
    queryKey: [
      'business-traveller',
      'employee-societe-progress',
      businessId,
      employee?.id,
      employee?.customerId,
      employee?.creditCustomerId,
    ],
    queryFn: async ({ signal }) => {
      const [qualifiedOrders, account] = await Promise.all([
        runLimited(() => getEmployeeQualifyingOrderCount(businessId, employee.customerId, signal), signal),
        runLimited(() => getLuxLoyaltyAccount(employee.creditCustomerId), signal),
      ]);
      return { qualifiedOrders, tier: account?.tier ?? null };
    },
    enabled: !!employee?.customerId && !!employee?.creditCustomerId && employee.status === 'ACTIVATED',
    staleTime: 60_000,
    retry: false,
  });
}
